import { useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { API_URL } from '../utils/config';
import { useOTPAuth } from '../auth/providers/OTPAuthProvider';

// User info interface
export interface UserInfo {
  id: string;
  email: string;
  role: string;
  roles: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
  permissions: string[];
}

// Cached user data fetcher - ensures only one API call per minute
let userDataPromise: Promise<UserInfo | null> | null = null;
let userDataTimestamp = 0;
let cachedUserData: UserInfo | null = null;
const USER_CACHE_DURATION = 60 * 1000; // 1 minute

// Current user fetcher with authentication
export const useCurrentUser = () => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;
  
  return useQuery<UserInfo | null, Error>({
    // Use a stable query key
    queryKey: ['currentUser'],
    queryFn: async (): Promise<UserInfo | null> => {
      try {
        if (!isAuthenticated) {
          return null;
        }

        // Check if we have valid cached data
        const now = Date.now();
        if (cachedUserData && (now - userDataTimestamp) < USER_CACHE_DURATION) {
          return cachedUserData;
        }

        // If a request is already in progress, wait for it
        if (userDataPromise) {
          return await userDataPromise;
        }

        // Start a new request
        userDataPromise = (async () => {
          try {
            const url = `${API_URL}/api/general/me`;
            const response = await authenticatedFetch(url);
            
            if (!response) {
              console.error('Failed to fetch user info: No response from authenticatedFetch');
              return null;
            }
            
            if (!response.ok) {
              console.error(`Failed to fetch user info: HTTP ${response.status} ${response.statusText}`);
              const text = await response.text();
              console.error('Response body:', text);
              return null;
            }

            const data = await response.json();
            const userData = data.data || null;
            
            // Update cache
            cachedUserData = userData;
            userDataTimestamp = Date.now();
            
            return userData;
          } finally {
            // Clear the promise so future requests can start fresh
            userDataPromise = null;
          }
        })();

        return await userDataPromise;
      } catch (error) {
        console.error('Current user fetch error:', error);
        return null;
      }
    },
    enabled: isAuthenticated, // Enable for both Microsoft and OTP auth
    staleTime: USER_CACHE_DURATION, // Consider user data fresh for 1 minute
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    refetchOnMount: false, // Don't refetch when component mounts if data is still fresh
    refetchInterval: false, // Disable automatic refetching
    networkMode: 'offlineFirst', // Use cached data first, then network
  });
};

// Function to invalidate the user cache (for use after checkout or other events)
export const invalidateUserCache = () => {
  cachedUserData = null;
  userDataTimestamp = 0;
  userDataPromise = null;
};

// Dashboard stats fetcher with authentication
export const useDashboardStats = () => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      try {
        // Only attempt authenticated fetch if user is logged in
        if (!isAuthenticated) {
          return getMockDashboardStats();
        }

        const response = await authenticatedFetch(`${API_URL}/api/sandbox/dashboard/stats`);
        
        if (!response) {
          return getMockDashboardStats();
        }

        if (!response.ok) {
          return getMockDashboardStats();
        }

        const data = await response.json();
        return data.data || data;
      } catch (_error) {
        void _error;
        return getMockDashboardStats();
      }
    },
    enabled: true, // Always enabled, will use mock data if not authenticated
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: false, // Don't retry on failure, fallback to mock data
  });
};

// User analytics fetcher with authentication
export const useUserAnalytics = (dateRange?: {
  startDate?: string;
  endDate?: string;
}) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['userAnalytics', dateRange],
    queryFn: async () => {
      try {
        // Only attempt authenticated fetch if user is logged in
        if (accounts.length === 0) {
          return getMockUserAnalytics();
        }

        const params = new URLSearchParams();
        if (dateRange?.startDate)
          params.append('startDate', dateRange.startDate);
        if (dateRange?.endDate) params.append('endDate', dateRange.endDate);

        const url = `${API_URL}/api/analytics/users${params.toString() ? '?' + params.toString() : ''}`;
        const response = await authenticatedFetch(url);

        if (!response) {
          return getMockUserAnalytics();
        }

        if (!response.ok) {
          return getMockUserAnalytics();
        }

        const data = await response.json();
        return data.data || data;
      } catch (_error) {
        void _error;
        return getMockUserAnalytics();
      }
    },
    enabled: true, // Always enabled, will use mock data if not authenticated
    retry: false, // Don't retry on failure, fallback to mock data
  });
};

// Mock data fallbacks
const getMockDashboardStats = () => ({
  totalProjects: 45,
  activeConsultations: 12,
  completedProjects: 33,
  revenueThisMonth: 125000,
  clientSatisfactionScore: 4.8,
  teamUtilization: 87,
  recentActivities: [
    {
      id: '1',
      type: 'project_started',
      description: 'New hotel valuation project in Prague',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      client: 'Central European Hotels',
    },
    {
      id: '2',
      type: 'consultation_completed',
      description: 'Market analysis consultation completed',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      client: 'Alpine Resorts Group',
    },
    {
      id: '3',
      type: 'report_delivered',
      description: 'Feasibility study report delivered',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      client: 'Mediterranean Hotels Ltd',
    },
  ],
  upcomingDeadlines: [
    {
      id: '1',
      projectName: 'Vienna Hotel District Analysis',
      deadline: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'on_track',
    },
    {
      id: '2',
      projectName: 'Luxury Resort Valuation - Santorini',
      deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'at_risk',
    },
  ],
});

const getMockUserAnalytics = () => ({
  totalUsers: 1250,
  activeUsers: 875,
  newUsers: 45,
  userGrowthRate: 12.5,
  usersByRegion: [
    { region: 'North America', count: 1250, percentage: 100 },
  ],
  userEngagement: {
    averageSessionTime: 24.5,
    pagesPerSession: 3.2,
    bounceRate: 0.35,
  },
  topUserActions: [
    { action: 'view_hotel', count: 2340 },
    { action: 'search_hotels', count: 1890 },
    { action: 'contact_request', count: 234 },
  ],
});
